import { type ChangeEvent, useEffect, useState } from 'react';
import {
  ActionMetadataEntryPoint,
  type TourConfirmationType,
  type TourDuration,
} from '@zg-rentals/ts-z-calendar-client';
import { DatarouterError } from '@zg-rentals/web-base';
import { Modal, useToast } from '@zillow/constellation-10';

import EditListingTourConfigModalHeader from './components/EditListingTourConfigModalHeader';
import EditListingTourConfigModalBody from './components/EditListingTourConfigModalBody';
import EditListingTourConfigModalFooter from './components/EditListingTourConfigModalFooter';
import UnmaskedToast from '../UnmaskedToast';
import { EditListingTourConfigSteps } from '../../constants/EditListingTourConfigModalConstants';
import type { TimeZoneIds } from '../../constants/TourCalendar';
import useAvailabilityDates from '../../hooks/useAvailabilityDates';
import type { AvailabilityDate } from '../../types/TourAvailabilityCards';
import type useTourCalendarApi from '../../utils/tourCalendarHooks';
import { convertAvailabilityDatesToSetAvailabilityLiteDto } from '../../utils/tourCalendarWebApiBuilder';

type EditListingTourConfigModalProps = {
  step: EditListingTourConfigSteps;
  tourCalendarHook: ReturnType<typeof useTourCalendarApi>;
  onClose: () => void;
  onStepChange: (step: EditListingTourConfigSteps) => void;
  isInRelistingFlow?: boolean;
  onTourDurationSave?: () => void;
  onTourDurationCancel?: () => void;
  canAccessPostingPathTourDuration?: boolean;
};

const EditListingTourConfigModal = ({
  step,
  onStepChange,
  tourCalendarHook,
  onClose,
  isInRelistingFlow = false,
  onTourDurationSave,
  onTourDurationCancel,
  canAccessPostingPathTourDuration,
}: EditListingTourConfigModalProps) => {
  const {
    availabilityEvents,
    disableTouringForListing,
    enableTouringForListing,
    isTouringSchedulingEnabled: isTouringEnabledFromApi,
    setAvailabilitiesLite,
    storeListingTourDuration,
    timeZone,
    tourConfirmationType: tourConfirmationTypeFromApi,
    tourDuration,
  } = tourCalendarHook;

  const [currentStep, setCurrentStep] = useState(step);
  const [selectedBookingMethod, setSelectedBookingMethod] = useState<
    (typeof TourConfirmationType)[keyof typeof TourConfirmationType] | undefined
  >(undefined);
  const [selectedTimeZone, setSelectedTimeZone] = useState<TimeZoneIds | undefined>(undefined);
  const [selectedTourDuration, setSelectedTourDuration] = useState<TourDuration | undefined>(undefined);
  const [isTouringEnabled, setIsTouringEnabled] = useState(false);
  const [availabilityDates, setAvailabilityDates] = useAvailabilityDates(availabilityEvents);
  const [hasAvailabilityError, setHasAvailabilityError] = useState(false);
  const { enqueueToast } = useToast();
  const entryPoint = isInRelistingFlow
    ? ActionMetadataEntryPoint.REACTIVATION_LISTING
    : ActionMetadataEntryPoint.NEW_LISTING;

  const handleStepChange = (step: EditListingTourConfigSteps) => {
    setCurrentStep(step);
    onStepChange(step);
  };

  useEffect(() => {
    if (tourConfirmationTypeFromApi) {
      setSelectedBookingMethod(tourConfirmationTypeFromApi);
    }

    if (tourConfirmationTypeFromApi && timeZone) {
      setSelectedTimeZone(timeZone);
      setIsTouringEnabled(true);
    }

    if (tourDuration) {
      setSelectedTourDuration(tourDuration);
    }
  }, [tourConfirmationTypeFromApi, timeZone, tourDuration]);

  useEffect(() => {
    if (tourCalendarHook.initialZoneIdWithoutTourConfig && isInRelistingFlow) {
      setSelectedTimeZone(tourCalendarHook.initialZoneIdWithoutTourConfig);
    }
  }, [tourCalendarHook.initialZoneIdWithoutTourConfig, isInRelistingFlow]);

  const handleToggleEnableTouring = (event: ChangeEvent<HTMLInputElement>) => {
    setIsTouringEnabled(event.target.checked);
  };

  const handleSetAvailabilityDates = (availabilityDates: Array<AvailabilityDate>) => {
    const hasError = availabilityDates.some((date) => date.timeSlots.some((slot) => slot.errorMessage));
    setHasAvailabilityError(hasError);
    setAvailabilityDates(availabilityDates);
  };

  const handlePrimaryButtonClick = () => {
    if (currentStep === EditListingTourConfigSteps.EDIT_BOOKING_METHOD) {
      if (!isTouringEnabled) {
        // disable touring
        disableTouringForListing();
      } else if (isTouringEnabled && selectedBookingMethod && selectedBookingMethod !== tourConfirmationTypeFromApi) {
        // update confirmation type
        enableTouringForListing(selectedBookingMethod, timeZone, tourDuration, entryPoint);
      }
    } else if (currentStep === EditListingTourConfigSteps.EDIT_AVAILABILITY) {
      const setAvailabilitiesLiteDto = convertAvailabilityDatesToSetAvailabilityLiteDto(availabilityDates);
      setAvailabilitiesLite(setAvailabilitiesLiteDto);
    } else if (currentStep === EditListingTourConfigSteps.SET_BOOKING_METHOD && selectedBookingMethod) {
      if (canAccessPostingPathTourDuration) {
        return handleStepChange(EditListingTourConfigSteps.SET_TOUR_DURATION);
      } else if (tourCalendarHook.initialZoneIdWithoutTourConfig && isInRelistingFlow) {
        return handleStepChange(EditListingTourConfigSteps.SET_AVAILABILITY);
      }
      return handleStepChange(EditListingTourConfigSteps.SET_TIME_ZONE);
    } else if (currentStep === EditListingTourConfigSteps.SET_TOUR_DURATION) {
      if (tourCalendarHook.initialZoneIdWithoutTourConfig && isInRelistingFlow) {
        return handleStepChange(EditListingTourConfigSteps.SET_AVAILABILITY);
      }
      return handleStepChange(EditListingTourConfigSteps.SET_TIME_ZONE);
    } else if (currentStep === EditListingTourConfigSteps.SET_TIME_ZONE) {
      return handleStepChange(EditListingTourConfigSteps.SET_AVAILABILITY);
    } else if (currentStep === EditListingTourConfigSteps.EDIT_TOUR_DURATION) {
      onTourDurationSave?.();
      if (selectedTourDuration) {
        return storeListingTourDuration(selectedTourDuration)
          .then(() => {
            enqueueToast(
              <UnmaskedToast tone="info">
                Your tour availability was adjusted to match the change in tour length.
              </UnmaskedToast>,
            );
            return;
          })
          .finally(() => {
            onClose();
          });
      }
    } else if (
      currentStep === EditListingTourConfigSteps.SET_AVAILABILITY &&
      selectedBookingMethod &&
      selectedTimeZone
    ) {
      return enableTouringForListing(selectedBookingMethod, selectedTimeZone, selectedTourDuration, entryPoint)
        .then(() => {
          const setAvailabilitiesLiteDto = convertAvailabilityDatesToSetAvailabilityLiteDto(availabilityDates);
          return setAvailabilitiesLite(setAvailabilitiesLiteDto);
        })
        .catch((error) => {
          const errorMessage = DatarouterError.getDisplayMessage(error);
          enqueueToast(<UnmaskedToast tone="critical">{errorMessage}</UnmaskedToast>);
        })
        .finally(() => {
          onClose();
        });
    }

    return onClose();
  };

  const handleSecondaryButtonClick = () => {
    switch (currentStep) {
      case EditListingTourConfigSteps.SET_TOUR_DURATION:
        return handleStepChange(EditListingTourConfigSteps.SET_BOOKING_METHOD);
      case EditListingTourConfigSteps.SET_TIME_ZONE:
        if (canAccessPostingPathTourDuration) {
          return handleStepChange(EditListingTourConfigSteps.SET_TOUR_DURATION);
        }
        return handleStepChange(EditListingTourConfigSteps.SET_BOOKING_METHOD);
      case EditListingTourConfigSteps.SET_AVAILABILITY:
        return handleStepChange(EditListingTourConfigSteps.SET_TIME_ZONE);
      case EditListingTourConfigSteps.EDIT_TOUR_DURATION:
        onTourDurationCancel?.();
        return onClose();
      default:
        return onClose();
    }
  };

  const isPrimaryButtonDisabled = () => {
    if (currentStep === EditListingTourConfigSteps.SET_BOOKING_METHOD) {
      return !selectedBookingMethod;
    } else if (currentStep === EditListingTourConfigSteps.SET_TIME_ZONE) {
      return !selectedTimeZone;
    } else if (
      currentStep === EditListingTourConfigSteps.SET_AVAILABILITY ||
      currentStep === EditListingTourConfigSteps.EDIT_AVAILABILITY
    ) {
      return hasAvailabilityError;
    }

    return false;
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open, _event, reason) => open === false && reason !== 'click' && onClose()}
      header={<EditListingTourConfigModalHeader step={currentStep} />}
      body={
        <EditListingTourConfigModalBody
          availabilityDates={availabilityDates}
          isTouringEnabled={isTouringEnabled}
          isTouringEnabledFromApi={isTouringEnabledFromApi}
          step={currentStep}
          timeZone={selectedTimeZone}
          tourConfirmationType={selectedBookingMethod}
          tourDuration={selectedTourDuration}
          onSelectBookingMethod={setSelectedBookingMethod}
          onSelectTourDuration={setSelectedTourDuration}
          onSelectTimeZone={setSelectedTimeZone}
          onSetAvailabilityDates={handleSetAvailabilityDates}
          onToggleEnableTouring={handleToggleEnableTouring}
        />
      }
      footer={
        <EditListingTourConfigModalFooter
          isPrimaryButtonDisabled={isPrimaryButtonDisabled()}
          step={currentStep}
          onPrimaryButtonClick={handlePrimaryButtonClick}
          onSecondaryButtonClick={handleSecondaryButtonClick}
        />
      }
      dividers
      shouldAwaitInteractionResponse
      closeButton={<span hidden />}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default EditListingTourConfigModal;
